// @ts-ignore: A current TS setting requires every file to be an exported module, but this file should be an iife
import "./vibes_host.scss";
import VibesListener from "src/shared/VibesListener";
import VibesHostEvents from "src/host/VibesHostEvents";
import { addToVibesState, initVibesState } from "src/host/VibesState";
import TrackingRepository from "src/shared/TrackingRepository";
// @ts-ignore loading the URL for the vibes css
import VibesHostCSSUrl from "../../configuration/entrypoint-linker-loader?entryPoint=vibes_host&assetType=css!";
import { getCsrfToken, getMonsSiteName } from "src/shared/getMetaData";
import { getVibesFeatureFlag } from "src/shared/featureFlags";
import EligibilityService from "src/shared/EligibilityService";
import { EventService, Events } from "src/shared/EventService";
import LocalCache from "src/shared/LocalCache";
import Targeter from "src/host/Targeter";
import TargetingService from "src/host/TargetingService";

interface ueLogErrorOptions {
    logLevel: string;
    attribution: string;
    message: string;
}

declare global {
    interface Window {
        // @ts-ignore Explicit any for error is ok
        ueLogError?: (error: unknown, opts: ueLogErrorOptions) => void;
    }
}

// eslint-disable-next-line
interface VibesTrackingEvent extends Event {
    detail: {
        workflow: string;
        ttl: number;
    };
}

function hideModal(modal: HTMLElement) {
   modal.classList.remove('vibes-modal-visible');
}

// addCSS returns a promise that is resolved when the cssTag is loaded. This allows us to wait for the CSS to load before
// we load the javascript. Will reject if there is an error with CSS tag.
function addCss(doc: Document, vibesUrl: string, vibesCSS: string): Promise<void> {
    return new Promise<void>((res, reject) => {
        // In development, using the webpack dev server inlines CSS and doesn't have a CSS file output. If we are running
        // locally, just resolve.
        // TODO: figure out how to get the CSS files to output from the webpack dev server.
        if (process.env.NODE_ENV !== "production") {
            return res();
        }
        const cssTag = doc.createElement("link");
        cssTag.onload = () => res();
        cssTag.onerror = ( e) => reject(e);
        cssTag.setAttribute("rel", 'stylesheet');
        cssTag.setAttribute("href", `${vibesUrl}/${vibesCSS}`);
        const appendTo = doc.getElementsByTagName("head")[0];
        appendTo.appendChild(cssTag);
    });
}

async function createModal(workflow: string, askMeLater: boolean): Promise<[HTMLElement, HTMLIFrameElement, HTMLElement]> {
    const treatment = await getVibesFeatureFlag();
    const modalClass = treatment === 'T3' ? 'experimental-treatment' : 'default-treatment';

    const container = document.createElement('div');
    container.id = 'vibes-modal-container';

    const modalWindow = document.createElement('div');
    modalWindow.id = "vibes-modal-window";
    modalWindow.classList.add(modalClass);

    modalWindow.setAttribute('tab-index', '-1');
    modalWindow.setAttribute('aria-role', 'dialog');
    modalWindow.setAttribute('aria-modal', 'true');
    modalWindow.setAttribute('aria-labeledby', 'vibes-modal-title');

    const modalHeader = document.createElement('div');
    modalHeader.id = 'vibes-modal-header';

    const title = document.createElement('h3');
    title.id = 'vibes-modal-title';

    // This default title will be updated with a translated value which is sent from the iframe
    title.innerText = 'We love feedback!';

    const closeButton = document.createElement('button');
    closeButton.id = 'vibes-close-button';
    closeButton.innerHTML = `<span class="vibes-close-button-caption">Close</span>`;

    const modalContent = document.createElement('div');
    modalContent.id = 'vibes-modal-content';

    const iframe = document.createElement('iframe');
    iframe.id = "vibes-modal-iframe";

    let src = `${process.env.HOST_FORM_URL!}?w=${workflow}`;
    if (askMeLater) {
        src += '&askMeLater=true';
    }

    iframe.src = src;

    modalContent.appendChild(iframe);

    modalHeader.appendChild(title);
    modalHeader.appendChild(closeButton);

    modalWindow.appendChild(modalHeader);
    modalWindow.appendChild(modalContent);

    container.appendChild(modalWindow);

    const close = () => {
        if (iframe.contentWindow) {
            iframe.contentWindow.postMessage({
                action: 'close',
                source: 'Vibes',
                args: [workflow]
            }, '*');
        }

        hideModal(container);
    };

    closeButton.addEventListener('click', () => {
       close();
    });

    modalWindow.addEventListener("keydown", (event) => {
        if (event.code === "Escape") {
            close();
        }
    });

    container.addEventListener('click', (e) => {
        const target = <HTMLElement>e.target;

        if (target.id === 'vibes-modal-container') {
            hideModal(target);
        }
    });

    // the container, iframe and title may be manipulated after creation
    return [container, iframe, title];
}

function addElementsToDocument(modal: HTMLElement, iframe: HTMLIFrameElement, workflow: string) {
    modal.addEventListener('close', () => {
        if (iframe.contentWindow) {
            iframe.contentWindow.postMessage({
                action: 'close',
                source: 'Vibes',
                args: [workflow]
            }, '*');
        }
    });

    document.body.appendChild(modal);
}

function getPlatformWorkflow() {
    const site = getMonsSiteName(document);

    if (site === 'sellerCentral') {
        return 'sellerCentralPlatform';
    }

    if (site === 'vendorCentral') {
        return 'vendorCentralPlatform';
    }

    return '';
}

async function setupModal(workflow: string, askMeLater: boolean) {
    const iSolemnlySwearToLoadCSS = addCss(document, "{{CloudFrontProtocol}}://{{CloudFrontDomain}}", VibesHostCSSUrl);
    const [modal, iframe, title] = await createModal(workflow, askMeLater);
    const events = new VibesHostEvents(modal, title);
    const listener = new VibesListener(window, events.handlers());

    await iSolemnlySwearToLoadCSS;
    addElementsToDocument(modal, iframe, workflow);
    listener.init();
    logDisplaySurveyMetrics(workflow);
}

async function runVibesTargeting() {
    const cache = new LocalCache(window.localStorage);

    const targeter = new Targeter(
        new TargetingService(process.env.API_HOST!),
        new EligibilityService(process.env.API_HOST!, cache),
        cache,
        new TrackingRepository(window.localStorage),
        new EventService(process.env.API_HOST!, ''),
    );

    const [workflow, askMeLater] = await targeter.shouldTarget();

    if (workflow) {
        await setupModal(workflow, askMeLater);
    }
}

async function vibesInit(): Promise<void> {
    try {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const debugVibesWorkflow = urlParams.get('debugVibesWorkflow');
        const debugAskMeLater = urlParams.get('debugVibesAskMeLater');

        if (debugVibesWorkflow) {
            await setupModal(debugVibesWorkflow, !!debugAskMeLater);
            return;
        }

        console.log('Initializing Vibes Host File');
        initVibesState();

        await runVibesTargeting();
    } catch (e) {
        if (window.ueLogError) {
            window.ueLogError(e, {
                logLevel: 'ERROR',
                attribution: 'Vibes',
                message: 'Unexpected exception caught while running Vibes'
            });
        } else {
            console.error("Error initializing Vibes host: ", e);
        }
    }
}

function logDisplaySurveyMetrics(workflow: string) {
    (async () => {
        try {
            const service = new EventService(process.env.API_HOST!, getCsrfToken(window.document));
            await service.saveEvent(Events.formWillShow, workflow);
        } catch (e) {
            if (window.ueLogError) {
                window.ueLogError(e, {
                    logLevel: 'ERROR',
                    attribution: 'Vibes',
                    message: 'Unexpected error saving form display event'
                });
            }
        }
    })();
}

(async function() {
    await vibesInit();
})();
